// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Service Layer
// Calls topsreliabilityframework-automationapi, this platform's own dedicated
// Automation Insights service (cutover 2026-08-25 — previously called the
// legacy standalone Automation Dashboard backend via a second, independently-
// scoped auth token; that bridge — services/automationInsightsAuth.ts — has
// been retired and deleted, see PHASE_11_BACKEND_MIGRATION_PLAN.md §2.1/§6.4).
// Endpoint shapes
// match the new service's routers/automation.py.
// ─────────────────────────────────────────────────────────────────────────────

import { AUTOMATION_INSIGHTS_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';
import {
  MOCK_PERFORMANCE_ALERTS,
  MOCK_PLATFORM_PERFORMANCE,
  buildMockPlatformSummary,
} from './automationInsightsMockData';

/**
 * Local-dev-only escape hatch: when set, all three Automation Insights panels
 * render fixture data instead of calling the real backend. Enable via
 * REACT_APP_AUTOMATION_USE_DUMMY_DATA=true in a local .env (git-ignored).
 */
export const AUTOMATION_INSIGHTS_USE_DUMMY_DATA = process.env.REACT_APP_AUTOMATION_USE_DUMMY_DATA === 'true';

export type AutomationTicketType = 'INC' | 'RITM';

export interface PerformanceAlertsParams {
  /** Platform query_key, e.g. 'O&E_INT_OCEAN', or 'all' */
  platform: string;
  ticketType: AutomationTicketType;
  /**
   * Shared time filter, matching the standalone dashboard's contract:
   * - 'day': requires dateFrom + dateTo
   * - 'week' | 'month': requires selectedPeriods (e.g. ['2026-W14'] or ['2026-04'])
   * No lookbackWeeks fallback here — AutomationFilterContext's default state
   * and validateFilterSelection() guarantee one of the two shapes above is
   * always well-formed before a request fires, so the old backend's
   * normalize/fallback logic is unnecessary on this side.
   */
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Rebuilt 2026-09-28, basis corrected 2026-10-04 — see
 * topsreliabilityframework-automationapi's automation/src/metrics.py
 * "v3: Performance Verdict Engine" for the full design. Current window is
 * always a rolling last 7 days (today excluded); baseline is a POOLED
 * `baseline_weeks` complete Monday-Sunday weeks before that (sum/sum and
 * mean-over-all-tickets, not an average of weekly rates). Both windows are
 * bucketed by each ticket's RESOLVED date, not when it was created — a
 * still-open ticket contributes to neither window until it resolves.
 */
export type PerformanceVerdict = 'Improved' | 'Degraded' | 'Mixed' | 'Inconclusive';

export interface PerformanceAlertWindow {
  current_start: string;
  current_end: string;
  baseline_start: string;
  baseline_end: string;
}

export interface PerformanceAlertItem {
  automation_name: string;
  verdict: PerformanceVerdict;
  recent_volume: number;
  baseline_volume: number;
  baseline_avg_weekly_volume: number;
  recent_fulfilled_count: number;
  baseline_fulfilled_count: number;
  delta_fulfillment: number | null;
  recent_fulfillment: number | null;
  baseline_fulfillment: number | null;
  delta_mttr_pct: number | null;
  recent_mttr: number | null;
  baseline_mttr: number | null;
  recent_mttr_ticket_count: number;
  baseline_mttr_ticket_count: number;
  /** True only on a "0% fulfillment" Degraded verdict reached despite volume below the adequacy floor. */
  low_volume_caveat?: boolean;
  reasons: string[];
  window: PerformanceAlertWindow;
}

export interface PerformanceAlertsResponse {
  platform: string;
  ticket_type: AutomationTicketType;
  date_from: string;
  date_to: string;
  baseline_weeks: number;
  degraded_count: number;
  improved_count: number;
  mixed_count: number;
  inconclusive_count: number;
  degraded: PerformanceAlertItem[];
  improved: PerformanceAlertItem[];
  mixed: PerformanceAlertItem[];
  inconclusive: PerformanceAlertItem[];
  verdicts: PerformanceAlertItem[];
}

/** Small artificial delay so dummy-data mode still shows loading skeletons. */
function simulateNetworkDelay(ms = 350): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function camelToSnakeCase(key: string): string {
  return key.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function buildQueryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(camelToSnakeCase(key), String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch improved/degraded automation performance alerts.
 * Mirrors performanceAlertsService.fetch() in the standalone repo's
 * services/service.ts. Authenticated with this platform's normal sign-in
 * token (authFetch) — no second scope, no second app registration.
 */
export async function fetchPerformanceAlerts(
  params: PerformanceAlertsParams
): Promise<PerformanceAlertsResponse> {
  if (AUTOMATION_INSIGHTS_USE_DUMMY_DATA) {
    await simulateNetworkDelay();
    return { ...MOCK_PERFORMANCE_ALERTS, platform: params.platform, ticket_type: params.ticketType };
  }

  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.PERFORMANCE_ALERTS}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform Performance Panel — types copied verbatim from the standalone
// repo's services/service.ts (PlatformPerformanceResponse and friends), so
// the field names/shape are guaranteed identical, not re-derived from memory.
// ─────────────────────────────────────────────────────────────────────────────

export type SourceFilterType = 'tori' | 'human' | 'all' | 'automation';

export interface PlatformPerformanceKPIData {
  vol_created: number;
  vol_resolved: number;
  auto_fulfilled: number;
  auto_fulfilled_pct: number;
  tori_vol: number;
  cne_count: number;
  // % of vol_created — same convention as auto_fulfilled_pct. Added
  // 2026-09-11 (backend has returned these since the same date; this port
  // just catches this live repo's types up to what automationenablementopena-
  // anedashboard already had).
  cne_pct: number | null;
  reopen_count: number;
  reopen_pct: number | null;
  avg_mttr: number;
  avg_mttr_p50: number;
  avg_mttr_p80: number;
  avg_mttr_p85: number;
  avg_mttr_p90: number;
}

export interface PlatformPerformanceWeeklyTrendPoint {
  week_start: string;
  vol_created: number;
  vol_resolved: number;
  auto_fulfilled_pct: number | null;
  tori_vol: number;
  cne_count: number;
  cne_pct: number | null;
  reopen_count: number;
  reopen_pct: number | null;
  avg_mttr: number;
}

export interface PlatformPerformanceSourceFilterPayload {
  kpis: PlatformPerformanceKPIData;
  weekly_trend: PlatformPerformanceWeeklyTrendPoint[];
  // daily_trend / monthly_trend also appear depending on time_filter_type;
  // left untyped-optional here since the old panel reads them defensively
  // too (see FlexibleTrendRow in the old PlatformPerformancePanel.tsx).
  daily_trend?: Array<Record<string, number | string | null>>;
  monthly_trend?: Array<Record<string, number | string | null>>;
}

export interface PlatformPerformanceResponse {
  platform: string;
  ticket_type: string;
  date_from: string;
  date_to: string;
  /** 'created' | 'resolved' — echoes back which basis this response was
   * computed with. Added 2026-10, RESOLVED_DATE_MIGRATION_PLAN.md. */
  basis?: AutomationBasis;
  source_filters: Partial<Record<SourceFilterType, PlatformPerformanceSourceFilterPayload>>;
}

/**
 * 'created' (default): week_start is created_date's week — the existing
 * behavior, unchanged. 'resolved': week_start is resolved_date's week
 * instead, so every KPI/trend point reflects "tickets resolved in this
 * week" rather than "tickets created in this week". See
 * RESOLVED_DATE_MIGRATION_PLAN.md §2 for the full distinction and a worked
 * example. Added 2026-10.
 */
export type AutomationBasis = 'created' | 'resolved';

export interface PlatformPerformanceParams {
  platform: string;
  ticketType: AutomationTicketType;
  sourceFilter?: SourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  /** Real assignment-group name to scope to, or omit/undefined for the whole
   * platform ('__ALL__' server-side default). Added 2026-09-11 (Stage 4). */
  assignmentGroup?: string;
  /** Omit for 'created' (server default) — see AutomationBasis. Added 2026-10. */
  basis?: AutomationBasis;
}

/**
 * Fetch platform performance KPIs + trend series, keyed by source filter
 * (all/automation/tori/human). Mirrors platformPerformanceService.fetch() in
 * the standalone repo's services/service.ts. Authenticated with this
 * platform's normal sign-in token (authFetch).
 */
export async function fetchPlatformPerformance(
  params: PlatformPerformanceParams
): Promise<PlatformPerformanceResponse> {
  if (AUTOMATION_INSIGHTS_USE_DUMMY_DATA) {
    await simulateNetworkDelay();
    return { ...MOCK_PLATFORM_PERFORMANCE, platform: params.platform, ticket_type: params.ticketType };
  }

  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_PERFORMANCE}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Automation Breakdown table — per-automation performance, shown inside
// Platform Performance when a specific platform is selected. Added 2026-10.
// ─────────────────────────────────────────────────────────────────────────────

export interface AutomationHealth {
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  color: string;
  label: string;
  fulfillment_score: number;
  speed_score: number;
  satisfaction_score: number;
}

export interface AutomationBreakdownRow {
  automation_name: string;
  ticket_type: 'INC' | 'RITM';
  total: number;
  fulfilled: number;
  cne_escalated: number;
  loop: number;
  human_takeover: number;
  manual_only: number;
  pending_open: number;
  tori_raised: number;
  reopen_count: number;
  avg_reopens: number;
  top_failure_reason: string;
  top_failure_reason_count: number;
  avg_mttr: number | null;
  mttr_p90: number | null;
  fulfilled_pct: number | null;
  /** Null only if total is 0 for this row (shouldn't normally happen — a row only exists because it has tickets). */
  health: AutomationHealth | null;
}

export interface AutomationBreakdownResponse {
  platform: string;
  ticket_type: string;
  source_filter: SourceFilterType;
  date_from: string;
  date_to: string;
  basis: AutomationBasis;
  rows: AutomationBreakdownRow[];
}

export interface AutomationBreakdownParams {
  platform: string;
  ticketType: AutomationTicketType | 'ALL';
  sourceFilter?: SourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  basis?: AutomationBasis;
}

/**
 * Per-automation performance table for the Platform Performance panel.
 * Only meaningful for a specific platform (not 'all') — callers should gate
 * on that themselves, same as the rest of this panel's platform-scoped data.
 */
export async function fetchAutomationBreakdown(
  params: AutomationBreakdownParams
): Promise<AutomationBreakdownResponse> {
  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.AUTOMATION_BREAKDOWN}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform Summary Panel — types copied verbatim from the standalone repo's
// components/platform-summary/PlatformSummaryPanel.tsx.
// ─────────────────────────────────────────────────────────────────────────────

export interface PlatformSummaryMetrics {
  vol_created: number;
  vol_resolved: number;
  auto_fulfilled: number;
  tori_vol: number;
  cne_count: number;
  reopen_count: number;
  /** All-time count of currently-open tickets (resolved_date IS NULL),
   * scoped by source_filter only — independent of the date-range filter AND
   * the Created/Resolved basis toggle (an open ticket has no resolved_date).
   * Populated on both get_panel_platform_table()'s top-level platform rows
   * and get_panel_platform_table_groups()'s assignment-group drill-down
   * rows. Added 2026-10. */
  open_tickets?: number;
  /** Resolved View only (basis='resolved') — RESOLVED_DATE_MIGRATION_PLAN.md
   * §2.4/§5.3, added 2026-10. Undefined/null under basis='created' (no
   * Overall/L1 split there). l1_resolved = resolved while the ticket's
   * current/final assignment_group was one of our own tracked queues.
   * overall_resolved = resolved after its SLA history touched one of our
   * tracked queues at any point (always >= l1_resolved). resolution_pct =
   * l1_resolved / overall_resolved. */
  l1_resolved?: number;
  overall_resolved?: number;
  resolution_pct?: number;
  avg_mttr: number;
  avg_mttr_p50?: number;
  avg_mttr_p80?: number;
  avg_mttr_p85?: number;
  avg_mttr_p90?: number;
}

export interface PlatformSummaryRow extends PlatformSummaryMetrics {
  platform_key: string;
  platform_name: string;
  ticket_type: 'ALL' | 'INC' | 'RITM';
}

export interface PlatformSummaryData {
  ticket_type: 'ALL' | 'INC' | 'RITM';
  source_filter: SourceFilterType;
  date_from: string;
  date_to: string;
  /** 'created' | 'resolved' — echoes back which basis this response was
   * computed with. Added 2026-10, RESOLVED_DATE_MIGRATION_PLAN.md. */
  basis?: AutomationBasis;
  rows: PlatformSummaryRow[];
  grand_total?: PlatformSummaryMetrics;
}

export interface PlatformSummaryParams {
  platform: string;
  ticketType: 'ALL' | 'INC' | 'RITM';
  sourceFilter?: SourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  /** Omit for 'created' (server default) — see AutomationBasis. Fully
   * independent of every other control on this table. Added 2026-10. */
  basis?: AutomationBasis;
}

/**
 * Fetch the Platform Summary table (per-platform KPI rows + grand total).
 * Mirrors platformSummaryService.fetch() -> panelEndpoints.platformSummaryAll
 * in the standalone repo, which hits /automation/api/panels/platform-table.
 * Authenticated with this platform's normal sign-in token (authFetch).
 */
export async function fetchPlatformSummary(
  params: PlatformSummaryParams
): Promise<PlatformSummaryData> {
  if (AUTOMATION_INSIGHTS_USE_DUMMY_DATA) {
    await simulateNetworkDelay();
    return { ...buildMockPlatformSummary(params.ticketType), source_filter: params.sourceFilter ?? 'all' };
  }

  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_SUMMARY}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Assignment-group drill-down (Stage 4, Platform View only) — added 2026-09-11.
// Application View is out of scope until the platform/application ↔
// assignment-group mapping is finalized (see AUTOMATION_INSIGHTS_MIGRATION_
// CHECKLIST.md Phase 12) — Platform View has no such ambiguity, since
// assignment_group is a direct per-ticket field, not a many-to-many join.
// ─────────────────────────────────────────────────────────────────────────────

export interface AssignmentGroupsResponse {
  platform: string;
  ticket_type: string;
  groups: string[];
}

/** Distinct real assignment-group names for one platform + ticket_type. Not
 * time-filtered by design — see the backend's get_assignment_groups_for_platform()
 * docstring. Feeds Platform Performance's dropdown and Platform Summary's
 * "does this platform have anything to expand" check. */
export async function fetchAssignmentGroupsForPlatform(
  platform: string,
  ticketType: string
): Promise<AssignmentGroupsResponse> {
  if (AUTOMATION_INSIGHTS_USE_DUMMY_DATA) {
    await simulateNetworkDelay(150);
    return { platform, ticket_type: ticketType, groups: [] };
  }

  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.ASSIGNMENT_GROUPS}${buildQueryString({ platform, ticketType })}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

export interface PlatformTableGroupRow extends PlatformSummaryMetrics {
  assignment_group: string;
}

export interface PlatformTableGroupsResponse {
  platform_key: string;
  platform_name: string;
  ticket_type: string;
  source_filter: string;
  date_from: string;
  date_to: string;
  /** 'created' | 'resolved' — echoes back which basis this response was
   * computed with. Added 2026-10, RESOLVED_DATE_MIGRATION_PLAN.md. */
  basis?: AutomationBasis;
  rows: PlatformTableGroupRow[];
}

export interface PlatformTableGroupsParams {
  platform: string;
  ticketType: 'ALL' | 'INC' | 'RITM';
  sourceFilter?: SourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  /** Must match the parent table's basis so expanded rows sum back to the
   * parent row. Omit for 'created' (server default). Added 2026-10. */
  basis?: AutomationBasis;
}

/** Assignment-group breakdown for ONE platform — the data behind an expanded
 * row in the Platform Summary table. Same column set as fetchPlatformSummary()'s
 * rows, just keyed by assignment_group instead of platform_key. */
export async function fetchPlatformTableGroups(
  params: PlatformTableGroupsParams
): Promise<PlatformTableGroupsResponse> {
  if (AUTOMATION_INSIGHTS_USE_DUMMY_DATA) {
    await simulateNetworkDelay(150);
    return {
      platform_key: params.platform,
      platform_name: params.platform,
      ticket_type: params.ticketType,
      source_filter: params.sourceFilter ?? 'all',
      date_from: '',
      date_to: '',
      rows: [],
    };
  }

  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_TABLE_GROUPS}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ── Ticket drill-down ─────────────────────────────────────────────────────────

export type { DrilldownTicket, DrilldownResponse } from './sapInsightsService';

export interface AutomationDrilldownParams {
  platform: string;
  column: string;
  basis: string;
  ticketType: string;
  sourceFilter: string;
  timeFilterType?: string;
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  assignmentGroup?: string;
  automationName?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchAutomationTicketDrilldown(
  params: AutomationDrilldownParams
): Promise<import('./sapInsightsService').DrilldownResponse> {
  const url = `${AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_TABLE_TICKETS}${buildQueryString(params)}`;
  const response = await authFetch(url);
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Automation drilldown error (${response.status}): ${body || response.statusText}`);
  }
  return response.json();
}
